import { Fragment } from 'react'
import CoachCellReference from './CoachCellReference'
import CoachTileReference from './CoachTileReference'
import type { CoachBoardFocus } from './coach.types'

type CoachCellReferenceConfig = {
  label: (cellNumber: number) => string
  actionLabel: (cellNumber: number) => string
  isPinned: (focus: CoachBoardFocus) => boolean
  onPreview: (focus: CoachBoardFocus) => void
  onPreviewEnd: () => void
  onActivate: (focus: CoachBoardFocus) => void
}

type CoachRichTextProps = {
  content: string
  tileLabel: (tile: string, rotation?: number) => string
  cellReference?: CoachCellReferenceConfig
}

const TILE_MARKER = /^\[\[tile:(t[1-6]):([0-3])\]\]$/i
const CELL_MARKER = /^\[\[cell:([1-9]|[1-4]\d)\]\]$/i
const RAW_TILE_KEY = /^t[1-6]$/i
const RAW_CELL_REFERENCE = /^(?:case|cell|casilla)\s+(?:n(?:°|º|o)\s*)?([1-9]|[1-4]\d)$/i

export default function CoachRichText({ content, tileLabel, cellReference }: CoachRichTextProps) {
  const parts = content.split(
    /(\[\[tile:t[1-6]:[0-3]\]\]|\[\[cell:(?:[1-9]|[1-4]\d)\]\]|\b(?:case|cell|casilla)\s+(?:n(?:°|º|o)\s*)?(?:[1-9]|[1-4]\d)\b|\*\*[^*\n]+\*\*|\bt[1-6]\b)/gi,
  )
  let latestTile: Pick<CoachBoardFocus, 'tile' | 'rotation'> | undefined

  return parts.map((part, index) => {
    const marker = TILE_MARKER.exec(part)
    if (marker) {
      const tile = marker[1].toLowerCase()
      const rotation = Number(marker[2])
      latestTile = { tile, rotation }
      return (
        <CoachTileReference
          key={index}
          tile={tile}
          rotation={rotation}
          label={tileLabel(tile, rotation)}
        />
      )
    }
    if (RAW_TILE_KEY.test(part)) {
      const tile = part.toLowerCase()
      latestTile = { tile }
      return <CoachTileReference key={index} tile={tile} label={tileLabel(tile)} />
    }

    const cellMatch = CELL_MARKER.exec(part) ?? RAW_CELL_REFERENCE.exec(part)
    if (cellMatch && cellReference) {
      const cellNumber = Number(cellMatch[1])
      const focus = { cellNumber, ...latestTile }
      return (
        <CoachCellReference
          key={index}
          focus={focus}
          label={cellReference.label(cellNumber)}
          actionLabel={cellReference.actionLabel(cellNumber)}
          isPinned={cellReference.isPinned(focus)}
          onPreview={cellReference.onPreview}
          onPreviewEnd={cellReference.onPreviewEnd}
          onActivate={cellReference.onActivate}
        />
      )
    }

    const isBold = part.startsWith('**') && part.endsWith('**')
    const text = isBold ? part.slice(2, -2) : part

    return isBold ? <strong key={index}>{text}</strong> : <Fragment key={index}>{text}</Fragment>
  })
}
